import { Text, View } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import { CButton, CInput } from '@app/components/Common';
import { Button, Input, Select } from 'antd';
import BigNumber from 'bignumber.js';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SETTING_TYPE_TRADE } from '../../../../../../constants/SETTING';
import { useIndexPrice } from '../../../../../../hooks/trade/indexprice';
import useTradeStore from '../../../../../../hooks/trade/tradeStore';
import { getSetting, useSetting } from '../../../../../../hooks/user/mysetting';
import { CustomInput, OrderValueInputSection, Wrapper } from '../styles';
import { useBinanceWebSocketStore } from '../../../../../../hooks/binanceWebsocketStore';

const OrderPriceInput = ({ limit_market, isManualInputRef, isMobile }) => {
  const { textw, gray, drawerBgColor, mobileTradeBorder, textsilver } =
    React.useContext(ThemeContext).theme.COLORS;
  const {
    isDarkMode,
    theme: { COLORS },
  } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';

  const { t } = useTranslation();
  const { data: tradeStore, mutate: mutateTradeStore } = useTradeStore();
  const { getPrice } = useIndexPrice(tradeStore?.ep_id);

  const { data: setting, updateSetting } = useSetting(SETTING_TYPE_TRADE);
  const alarm = getSetting(setting, 'ALM' + tradeStore?.ep_id, 0);

  const [trigger, setTrigger] = React.useState('BIG');

  // 이전 ep_id 추적 (코인 변경 감지용)
  const prevEpIdRef = useRef<number | undefined>(undefined);

  /**
   * ✅ 버튼 클릭 시 실시간 가격 가져오기 (항상 최신 값)
   * Zustand store에서 직접 읽어서 클릭 순간의 최신 가격 반환
   */
  const getCurrentPrice = React.useCallback(() => {
    // ✅ getState()로 클릭 순간의 최신 가격 가져오기
    const { tradePrice } = useBinanceWebSocketStore.getState();

    if (tradePrice) {
      console.log('[OrderPriceInput] 실시간 가격:', tradePrice);
      return BigNumber(tradePrice).toNumber();
    }

    // Fallback: SWR 캐시
    const fallbackPrice = getPrice(tradeStore?.ep_id);
    console.log('[OrderPriceInput] Fallback 가격:', fallbackPrice);
    return fallbackPrice;
  }, [getPrice, tradeStore?.ep_id]);

  // 코인(ep_id) 변경 시 order_price 초기화
  useEffect(() => {
    const currentEpId = tradeStore?.ep_id;

    // 코인이 변경된 경우 order_price 리셋
    if (prevEpIdRef.current !== undefined && prevEpIdRef.current !== currentEpId) {
      console.log('[OrderPriceInput] 코인 변경 감지:', prevEpIdRef.current, '→', currentEpId);
      mutateTradeStore({
        ...tradeStore,
        order_price: undefined,
      });
    }

    prevEpIdRef.current = currentEpId;
  }, [tradeStore?.ep_id]);

  // order_price가 없을 때 현재 가격으로 초기화
  useEffect(() => {
    if (
      tradeStore?.order_price === undefined ||
      tradeStore?.order_price === '' ||
      isNaN(tradeStore?.order_price) ||
      tradeStore?.order_price == 0
    ) {
      const price = getCurrentPrice();
      if (price) {
        mutateTradeStore({
          ...tradeStore,
          order_price: BigNumber(price).toFixed(tradeStore?.dp || 3),
        });
      }
    }
  }, [tradeStore?.order_price, tradeStore?.dp, getCurrentPrice]);

  const setOrderPrice = (data) => {
    if (isNaN(data)) return;
    mutateTradeStore({ ...tradeStore, order_price: data });
  };

  const setTriggerPrice = (data) => {
    if (isNaN(data)) return;
    const price = getCurrentPrice();
    const trigger = price > data ? 'BIG' : 'SML';

    mutateTradeStore({
      ...tradeStore,
      trigger_price: data,
      trigger_type: trigger,
    });
  };

  const handleTriggerButton = (type: 'plus' | 'minus') => {
    if (type === 'plus') {
      setTriggerPrice(
        BigNumber(tradeStore?.trigger_price || 0)
          .plus(1)
          .toNumber()
      );
    } else {
      setTriggerPrice(
        BigNumber(tradeStore?.trigger_price || 0)
          .minus(1)
          .toNumber()
      );
    }
  };

  return (
    <OrderValueInputSection>
      {/** 트리거 주문 지정가 및 시장가 선택 버튼 */}
      {(limit_market == 'T' ||
        limit_market == 'CL' ||
        limit_market == 'CM') && (
        <View
          style={{
            flexDirection: 'row',
            width: '100%',
            gap: 8,
          }}
        >
          <Button
            style={{
              color: limit_market == 'CL' ? '#07D993' : '#777986',
              background:
                limit_market == 'CL'
                  ? 'rgba(7, 217, 147, 0.2)'
                  : 'rgba(42, 44, 46, 0.5)',
              border: `0px`,
              borderRadius: 6,
              flex: 1,
              fontFamily: 'NanumSquareRound',
              fontSize: 12,
              lineHeight: 1,
              fontWeight: 400,
              letterSpacing: -0.24,
              height: 32,
            }}
            onClick={() => {
              mutateTradeStore({
                ...tradeStore,
                limit_market: 'CL',
              });
            }}
          >
            {t('TradePage.Limit')}
          </Button>

          <Button
            style={{
              color: limit_market == 'CM' ? '#07D993' : '#777986',
              background:
                limit_market == 'CM'
                  ? 'rgba(7, 217, 147, 0.2)'
                  : 'rgba(42, 44, 46, 0.5)',
              border: `0px`,
              borderRadius: 6,
              flex: 1,
              fontFamily: 'NanumSquareRound',
              fontSize: 12,
              lineHeight: 1,
              fontWeight: 400,
              letterSpacing: -0.24,
              height: 32,
            }}
            onClick={() => {
              mutateTradeStore({
                ...tradeStore,
                limit_market: 'CM',
              });
            }}
          >
            {t('TradePage.Market')}
          </Button>
        </View>
      )}

      {/** 주문 가격 입력 (Order Price Input) */}
      {limit_market != 'CM' && (
        <>
          {isMobile ? (
            <Wrapper>
              <Text
                style={{
                  color: '#B3B5BD',
                  fontSize: 12,
                  fontFamily: 'NanumSquareRound',
                  fontWeight: 400,
                  letterSpacing: -0.36,
                }}
              >
                {t('orderPrice')}
              </Text>
              <CustomInput
                placeholder={t('orderPrice')}
                value={tradeStore?.order_price ? tradeStore?.order_price : null}
                type={'text'}
                onChange={(e: any) => setOrderPrice(e.target.value)}
                suffix={
                  <CButton
                    title={t('lastprice')}
                    onClick={() => {
                      const currentPrice = getCurrentPrice();
                      if (currentPrice) {
                        setOrderPrice(
                          BigNumber(currentPrice).toFixed(tradeStore?.dp || 3)
                        );
                      }
                    }}
                    style={{
                      color: '#07D993',
                      fontFamily: 'NanumSquareRound',
                      fontWeight: 700,
                      fontSize: 12,
                      letterSpacing: -0.24,
                      cursor: 'pointer',
                    }}
                  />
                }
              />
            </Wrapper>
          ) : (
            <Wrapper>
              <Text
                style={{
                  color: '#B3B5BD',
                  fontSize: 12,
                  fontFamily: 'NanumSquareRound',
                  fontWeight: 400,
                  letterSpacing: -0.36,
                }}
              >
                {t('orderPrice')}
              </Text>

              <CustomInput
                placeholder={t('orderPrice')}
                value={tradeStore?.order_price ? tradeStore?.order_price : null}
                type={'text'}
                onChange={(e: any) => setOrderPrice(e.target.value)}
                suffix={
                  <CButton
                    title={t('lastprice')}
                    onClick={() => {
                      // ✅ Realtime 서버의 실시간 가격 사용
                      const currentPrice = getCurrentPrice();
                      if (currentPrice) {
                        setOrderPrice(
                          BigNumber(currentPrice).toFixed(tradeStore?.dp || 3)
                        );
                      }
                    }}
                    style={{
                      color: '#07D993',
                      fontFamily: 'NanumSquareRound',
                      fontWeight: 700,
                      fontSize: 12,
                      letterSpacing: -0.24,
                      cursor: 'pointer',
                    }}
                  />
                }
              />
            </Wrapper>
          )}
        </>
      )}

      {/** 트리거 주문 가격 입력 (Trigger Price Input) */}
      {(limit_market == 'T' || limit_market == 'CM' || limit_market == 'CL') &&
        (isMobile ? (
          <Wrapper style={{ marginTop: 8 }}>
            <Text
              style={{
                color: '#B3B5BD',
                fontSize: 12,
                fontFamily: 'NanumSquareRound',
                fontWeight: 400,
                letterSpacing: -0.36,
              }}
            >
              {t('triggerPrice')}
            </Text>
            <CustomInput
              value={
                tradeStore?.trigger_price ? tradeStore?.trigger_price : null
              }
              type={'text'}
              onChange={(e: any) => setTriggerPrice(e.target.value)}
              suffix={
                <div
                  style={{
                    color: '#E9E9ED',
                    fontFamily: 'NanumSquareRound',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: -0.24,
                  }}
                >
                  USDT
                </div>
              }
            />
          </Wrapper>
        ) : (
          <Wrapper>
            <Text
              style={{
                color: '#B3B5BD',
                fontSize: 12,
                fontFamily: 'NanumSquareRound',
                fontWeight: 400,
                letterSpacing: -0.36,
              }}
            >
              {t('triggerPrice')}
            </Text>

            <CustomInput
              value={
                tradeStore?.trigger_price ? tradeStore?.trigger_price : null
              }
              type={'text'}
              onChange={(e: any) => setTriggerPrice(e.target.value)}
            />
          </Wrapper>
        ))}
    </OrderValueInputSection>
  );
};

export default React.memo(OrderPriceInput);
